import { createContext, useContext, useMemo } from "react";
import type { PropsWithChildren } from "react";
import { useFormContext } from "react-hook-form";
import { useQuery } from "react-query";
import { paywallApi } from "@circle-react/api/paywallApi";
import type { PaywallForm } from "../../types/PaywallForm";
import { PAYWALL_STATUS } from "@circle-react/types/Paywall";

interface UpsellTabProviderProviderProps {
  offeredPaywall?: PaywallForm;
  offerStatus?: string;
  offerType?: string;
  paywallOptions: Array<{ label: string; value: number | string }>;
  paywallSearchQuery?: ReturnType<typeof useQuery<Record<string, any>>>;
}

const UpsellTabProvider = createContext<UpsellTabProviderProviderProps>({
  paywallOptions: [],
});

UpsellTabProvider.displayName = "UpsellTabProvider";

export const useUpsellTabContext = () => useContext(UpsellTabProvider);

export const UpsellTabProviderContext = ({
  children,
  ...props
}: PropsWithChildren<unknown>) => {
  const { watch } = useFormContext<PaywallForm>();
  const paywallForm = watch();

  const queryParams = {
    params: {
      filters: {
        status: PAYWALL_STATUS.ACTIVE,
      },
      query: {
        ...(paywallForm.paywall_group_id
          ? {
              not_subscription_group: paywallForm.paywall_group_id,
            }
          : {}),
      },
    },
  };

  const paywallSearchQuery = useQuery<Record<string, any>>(
    ["upsell_tab-paywalls_list"],
    paywallApi.search(queryParams),
  );

  const offeredPaywallId = watch(
    "paywall_offers_attributes.offered_paywall_id",
  );
  const offerStatus = watch("paywall_offers_attributes.status");
  const offerType = watch("paywall_offers_attributes.offer_type");

  const offeredPaywall = paywallSearchQuery?.data?.records?.find(
    (paywall: PaywallForm) => paywall.id === offeredPaywallId,
  );

  const paywallOptions = useMemo(
    () =>
      paywallSearchQuery?.data?.records?.map((paywall: PaywallForm) => ({
        label: paywall.name || paywall.display_name,
        value: paywall.id,
      })) || [],
    [paywallSearchQuery?.data?.records],
  );

  const values = {
    paywallSearchQuery,
    offeredPaywall,
    paywallOptions,
    offerStatus,
    offerType,
    ...props,
  };

  return (
    <UpsellTabProvider.Provider value={values}>
      {children}
    </UpsellTabProvider.Provider>
  );
};
